import {
  InvoiceTransformationAvailableField,
  InvoiceTransformationRequest,
  InvoiceTransformationResponse,
  InvoiceTransformationTargetEnum,
} from '@monetize/types/app';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiGet, apiPost } from './axios';

export const invoiceTransformationQueryKeys = {
  base: ['invoiceTransformations'] as const,
  list: () => [...invoiceTransformationQueryKeys.base, 'list'] as const,
  getById: (billGroupId: string) =>
    [...invoiceTransformationQueryKeys.base, 'getById', billGroupId] as const,
  listFields: (target: InvoiceTransformationTargetEnum) =>
    [...invoiceTransformationQueryKeys.base, 'list-fields', target] as const,
};

export function useGetInvoiceTransformationById<
  SelectData = InvoiceTransformationResponse,
>(
  billGroupId: string,
  options: Partial<
    UseQueryOptions<InvoiceTransformationResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...invoiceTransformationQueryKeys.getById(billGroupId)],
    queryFn: () =>
      apiGet<InvoiceTransformationResponse>(
        `/api/billGroups/${billGroupId}/invoiceTransformations`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useGetInvoiceTransformationAvailableFields<
  SelectData = InvoiceTransformationAvailableField[],
>(
  target: InvoiceTransformationTargetEnum,
  options: Partial<
    UseQueryOptions<InvoiceTransformationAvailableField[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...invoiceTransformationQueryKeys.listFields(target)],
    queryFn: () =>
      apiGet<InvoiceTransformationAvailableField[]>(
        `/api/invoiceTransformations/availableFields`,
        { params: { target } },
      ).then((res) => res.data),
    ...options,
  });
}

export function useUpsertInvoiceTransformation(
  options: Partial<
    UseMutationOptions<
      InvoiceTransformationResponse,
      unknown,
      InvoiceTransformationRequest
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    InvoiceTransformationResponse,
    unknown,
    InvoiceTransformationRequest
  >({
    mutationFn: (payload) => {
      return apiPost<InvoiceTransformationResponse>(
        `/api/billGroups/invoiceTransformations`,
        payload,
      ).then((res) => res.data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: invoiceTransformationQueryKeys.getById(variables.billGroupId),
      });
      options.onSuccess?.(data, variables, context);
    },
  });
}
